import { Stack, Typography, Button, Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';

import { environment } from '@env';

import { DashboardStats } from '@/types/dashboard';

import { PropertyLocationType } from '@/api/enums';
import { Building01Icon } from '@/assets/icons/Building01Icon';
import { Mail02Icon } from '@/assets/icons/Mail02Icon';
import { Phone02Icon } from '@/assets/icons/Phone02Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatPhoneNumber } from '@/utils/phone';
import { isSingleFamilyEquivalentType } from '@/utils/propertyLocationTypeUtils';
import { getResolvedRoutePath } from '@/utils/router';

import DashboardExpiringLeases from '@/components/dashboard/DashboardExpiringLeases';
import DashboardSection from '@/components/dashboard/DashboardSection';
import DashboardStatsCard from '@/components/dashboard/DashboardStatsCard';
import DashboardStripeBalance from '@/components/dashboard/DashboardStripeBalance';
import DashboardUpcomingEvents from '@/components/dashboard/DashboardUpcomingEvents';
import LoadingState from '@/components/LoadingState';
import MapboxAddressMap from '@/components/mapbox/MapboxAddressMap';
import NoPropertyLocation from '@/components/property-manager-portal/NoPropertyLocation';
import { propertyLocationTypeOptions } from '@/components/propertyLocation/options';

const DashboardPage: React.FC = () => {
  const { session } = useAuth();
  const { isFetching, isOnboardingComplete } = useOnboardingProgress();
  const { selectedPropertyLocation, isPropertyOwner } = usePropertyManager();

  const { data: stats, isLoading: isStatsLoading } = useQuery({
    enabled:
      !!session?.access_token && !isFetching && isOnboardingComplete && !!selectedPropertyLocation,
    queryKey: ['GET', 'dashboard', 'stats', session?.access_token, selectedPropertyLocation?.id],
    queryFn: async () => {
      try {
        const response = await axios.get<DashboardStats>(
          `${environment.api}/dashboard/${selectedPropertyLocation?.id}/stats`,
          {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
            },
          }
        );
        return response.data;
      } catch (error) {
        return null;
      }
    },
  });

  const getPropertyTypeDisplayText = (type: PropertyLocationType): string => {
    if (isSingleFamilyEquivalentType(type)) {
      return 'Single Family Home';
    }

    if (type === PropertyLocationType.MULTI_UNIT) {
      return 'Multi Unit';
    }

    return propertyLocationTypeOptions.find(option => option.value === type)?.label || type;
  };

  if (isFetching) return <LoadingState />;

  // if (!isOnboardingComplete) return <Redirect to={PMPRoutes.onboardingChecklist} />;

  // Show NoPropertyLocation component if user doesn't have any property locations
  if (!selectedPropertyLocation) {
    return <NoPropertyLocation />;
  }

  return (
    <Stack spacing={3}>
      {selectedPropertyLocation && (
        <DashboardSection>
          <Stack
            spacing={2}
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              sm: 'center',
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', md: 280 },
                    height: 200,
                    flexShrink: 0,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <MapboxAddressMap addressText={selectedPropertyLocation.address_text} />
                </Box>
                <Stack spacing={2} flex={1}>
                  <Stack spacing={0.5}>
                    <Typography variant="h6" fontWeight={600}>
                      {selectedPropertyLocation.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedPropertyLocation.address_text}
                    </Typography>
                  </Stack>

                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Building01Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body3" color="text.primary">
                        {getPropertyTypeDisplayText(selectedPropertyLocation.type)}
                      </Typography>
                    </Stack>

                    {selectedPropertyLocation.phone && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Phone02Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body3" color="text.primary">
                          {formatPhoneNumber(selectedPropertyLocation.phone.value)}
                        </Typography>
                      </Stack>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Mail02Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography
                        variant="body3"
                        color={
                          selectedPropertyLocation.email?.value ? 'text.primary' : 'text.secondary'
                        }
                      >
                        {selectedPropertyLocation.email?.value || 'No email configured'}
                      </Typography>
                    </Stack>

                    {!isPropertyOwner && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        component={RouterLink}
                        size="small"
                        to={getResolvedRoutePath(PMPRoutes.propertyLocationView, {
                          id: String(selectedPropertyLocation.id),
                        })}
                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                      >
                        View Location Details
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </DashboardSection>
      )}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        sx={{
          flexWrap: 'wrap',
          '& > *': {
            minWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' },
          },
        }}
      >
        <DashboardStripeBalance isLoading={isStatsLoading} />

        {stats?.pendingEmailCount ? (
          <DashboardStatsCard
            isLoading={isStatsLoading}
            Icon={<Mail02Icon />}
            stats={[
              {
                label: 'Pending Emails',
                type: 'warning',
                value: stats?.pendingEmailCount || 0,
                valuePrimary: {
                  type: 'error',
                  value: stats?.emergencyEmailCount || 0,
                },
                valueSecondary: {
                  type: 'success',
                  value: stats?.completedEmailCount || 0,
                },
              },
              {
                label: 'Emergency Emails',
                type: 'error',
                value: stats?.emergencyEmailCount || 0,
                location: PMPRoutes.inboundMessagesWithTenants,
              },
              {
                label: 'In-Progress Emails',
                type: 'warning',
                value: stats?.pendingEmailCount || 0,
                location: PMPRoutes.inboundMessagesWithTenants,
              },
              {
                label: 'Completed Emails',
                type: 'success',
                value: stats?.completedEmailCount || 0,
                location: PMPRoutes.inboundMessagesWithTenants,
              },
            ]}
          />
        ) : null}

        {stats?.pendingSmsCount ? (
          <DashboardStatsCard
            isLoading={isStatsLoading}
            Icon={<Phone02Icon />}
            stats={[
              {
                label: 'Pending SMS',
                type: 'warning',
                value: stats?.pendingSmsCount || 0,
                valuePrimary: {
                  type: 'error',
                  value: stats?.emergencySmsCount || 0,
                },
                valueSecondary: {
                  type: 'success',
                  value: stats?.completedSmsCount || 0,
                },
              },
              {
                label: 'Emergency SMS',
                type: 'error',
                value: stats?.emergencySmsCount || 0,
                location: PMPRoutes.inboundMessagesWithTenants,
              },
              {
                label: 'In-Progress SMS',
                type: 'warning',
                value: stats?.pendingSmsCount || 0,
                location: PMPRoutes.inboundMessagesWithTenants,
              },
              {
                label: 'Completed SMS',
                type: 'success',
                value: stats?.completedSmsCount || 0,
                location: PMPRoutes.inboundMessagesWithTenants,
              },
            ]}
          />
        ) : null}

        {stats?.totalUnits && stats?.totalUnits > 1 ? (
          <DashboardStatsCard
            isLoading={isStatsLoading}
            Icon={<Building01Icon />}
            stats={[
              {
                label: 'Total Units',
                type: 'success',
                value: stats?.totalUnits || 0,
              },
            ]}
          />
        ) : null}

        {stats?.totalTenants ? (
          <DashboardStatsCard
            isLoading={isStatsLoading}
            Icon={<Users02Icon />}
            stats={[
              {
                label: 'Total Tenants',
                type: 'success',
                value: stats?.totalTenants || 0,
              },
            ]}
          />
        ) : null}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{
            width: '100%',
            '& > *': {
              flex: { md: 1, xs: 'unset' },
              height: '30rem',
            },
          }}
        >
          {/* <DashboardIssueSummary /> */}

          <DashboardExpiringLeases />

          <DashboardUpcomingEvents />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default DashboardPage;
